import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import StickyHead from "../StickyHead";

/* 라오나 접근 IP — 라오나키트(/laonabot)를 정식 오픈 전까지 등록한 IP 에서만 연다 (백엔드 app/laona_access.py ·
   app/sstoa/api/laona_access.py). 제한이 켜져 있으면 다른 IP 에는 "보다 나은 서비스를 위해 준비중입니다." 가 뜨고
   라오나키트 API 도 막힌다. 정식 오픈은 [접근 제한]을 끄면 된다. 한 줄에 IP 하나, 192.168.0.0/24 처럼 대역도 된다. */

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("ko-KR", { dateStyle: "short", timeStyle: "short" }) : "");

export default function LaonaAccessAdmin() {
  const [cfg, setCfg] = useState(null);
  const [ip, setIp] = useState("");
  const [memo, setMemo] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/laona-access").then(setCfg).catch((err) => setError(err.message));
  }, []);

  async function save(next, msg) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await api.put("/laona-access", { enabled: next.enabled, ips: next.ips.map(({ ip: i, memo: m }) => ({ ip: i, memo: m || "" })) });
      setCfg(r);
      setNotice(msg);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(e) {
    e?.preventDefault();
    const v = ip.trim();
    if (!v) return;
    if (await save({ ...cfg, ips: [...cfg.ips, { ip: v, memo: memo.trim() }] }, `${v} 을(를) 등록했습니다.`)) {
      setIp("");
      setMemo("");
    }
  }

  function toggle() {
    const enabled = !cfg.enabled;
    if (enabled && !cfg.ips.length && !window.confirm("등록된 IP 가 없습니다. 이대로 켜면 아무도 라오나키트를 쓸 수 없습니다. 켤까요?")) return;
    if (!enabled && !window.confirm("접근 제한을 끄면 누구나 라오나키트를 쓸 수 있습니다(정식 오픈). 끌까요?")) return;
    save({ ...cfg, enabled }, enabled ? "접근 제한을 켰습니다. 등록한 IP 에서만 라오나키트가 열립니다." : "접근 제한을 껐습니다. 누구나 라오나키트를 쓸 수 있습니다.");
  }

  function remove(target) {
    if (cfg.enabled && target === cfg.my_ip && !window.confirm("지금 접속한 IP 입니다. 지우면 이 컴퓨터에서도 라오나키트가 막힙니다. 지울까요?")) return;
    save({ ...cfg, ips: cfg.ips.filter((e) => e.ip !== target) }, `${target} 을(를) 지웠습니다.`);
  }

  function editMemo(target, value) {
    setCfg((c) => ({ ...c, ips: c.ips.map((e) => (e.ip === target ? { ...e, memo: value } : e)) }));
  }

  const myListed = cfg && cfg.ips.some((e) => e.ip === cfg.my_ip);

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>

        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">라오나 접근 IP</h1>
            <p className="sstoa-sub">
              라오나키트를 서비스 가능한 상태까지 개발하는 동안, 여기 등록한 IP 에서만 라오나키트(/laonabot)가 열립니다.
              다른 IP 에서는 ‘보다 나은 서비스를 위해 준비중입니다.’ 가 뜹니다. 정식 오픈할 때 [접근 제한]을 끄세요.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a className="sstoa-btn" href="/laonabot" target="_blank" rel="noreferrer">라오나키트 열기</a>
          </div>
        </div>
      </StickyHead>

      {notice && <div className="sstoa-hint" style={{ marginBottom: 12 }}>{notice}</div>}
      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      {cfg === null ? (
        <div className="sstoa-panel"><div className="sstoa-empty">{error ? "불러오지 못했습니다." : "불러오는 중…"}</div></div>
      ) : (
        <>
          <div className="sstoa-panel" style={{ padding: 16, marginBottom: 16, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 260px" }}>
              <b>접근 제한</b>{" "}
              <span className="sstoa-tag" style={cfg.enabled ? { color: "#a86f00", borderColor: "currentColor" } : { color: "#1d8549", borderColor: "currentColor" }}>
                {cfg.enabled ? "켜짐 — 등록한 IP 만" : "꺼짐 — 누구나"}
              </span>
              <div className="sstoa-hint" style={{ marginTop: 6 }}>
                지금 접속한 IP: <b style={{ fontFamily: "monospace" }}>{cfg.my_ip || "알 수 없음"}</b>
                {cfg.my_ip && (myListed ? " · 등록됨" : " · 등록 안 됨")}
              </div>
            </div>
            {cfg.my_ip && !myListed && (
              <button className="sstoa-btn" disabled={busy}
                      onClick={() => save({ ...cfg, ips: [...cfg.ips, { ip: cfg.my_ip, memo: "" }] }, `${cfg.my_ip} 을(를) 등록했습니다.`)}>
                지금 IP 등록
              </button>
            )}
            <button className={`sstoa-btn${cfg.enabled ? "" : " sstoa-btn--primary"}`} disabled={busy} onClick={toggle}>
              {cfg.enabled ? "접근 제한 끄기 (정식 오픈)" : "접근 제한 켜기"}
            </button>
          </div>

          <form className="sstoa-panel" onSubmit={add} style={{ padding: 16, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="sstoa-field" style={{ flex: "1 1 200px" }}>
              <span>IP 주소</span>
              <input className="sstoa-input" value={ip} onChange={(e) => setIp(e.target.value)} placeholder="예: 119.206.169.50 · 192.168.0.0/24" maxLength={64} />
            </label>
            <label className="sstoa-field" style={{ flex: "2 1 240px" }}>
              <span>메모</span>
              <input className="sstoa-input" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="예: 사무실 · 개발자 집" maxLength={100} />
            </label>
            <button className="sstoa-btn sstoa-btn--primary" type="submit" disabled={busy || !ip.trim()}>등록</button>
          </form>

          <div className="sstoa-panel">
            {cfg.ips.length === 0 ? (
              <div className="sstoa-empty">등록된 IP 가 없습니다.</div>
            ) : (
              <table className="sstoa-table">
                <thead>
                  <tr><th>IP 주소</th><th>메모</th><th>등록일</th><th></th></tr>
                </thead>
                <tbody>
                  {cfg.ips.map((e) => (
                    <tr key={e.ip}>
                      <td style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {e.ip}
                        {e.ip === cfg.my_ip && <span className="sstoa-tag sstoa-tag--gray" style={{ marginLeft: 6 }}>지금 접속</span>}
                      </td>
                      <td>
                        <input className="sstoa-input" value={e.memo || ""} maxLength={100}
                               onChange={(ev) => editMemo(e.ip, ev.target.value)}
                               onBlur={() => save(cfg, "메모를 저장했습니다.")} />
                      </td>
                      <td style={{ whiteSpace: "nowrap" }} className="sstoa-hint">{fmt(e.added_at)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button className="sstoa-btn sstoa-btn--sm" disabled={busy} onClick={() => remove(e.ip)}>삭제</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
